import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Navigate } from '@sorare/routing';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { HeadlineS, LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { PublicWatchlists } from '@sorare/marketplace/src/components/watchlists/PublicWatchlists';

import type {
  PlayerWatchlistsQuery,
  PlayerWatchlistsQueryVariables,
} from './__generated__/index.graphql.ts';

const PLAYER_WATCHLISTS_QUERY = gql`
  query PlayerWatchlistsQuery($slug: String!, $cursor: String) {
    anyPlayer(slug: $slug) {
      slug
      printablePlayer {
        id
      }
      publicWatchlists(first: 20, after: $cursor) {
        nodes {
          slug
          ...PublicWatchlists_watchlist
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PublicWatchlists.fragments.watchlist}
` as TypedDocumentNode<PlayerWatchlistsQuery, PlayerWatchlistsQueryVariables>;

type Props = {
  slug: string;
};

const StyledFullWidthContainer = styled(FullWidthContainer)`
  padding-top: var(--quadruple-unit);
  padding-bottom: var(--quadruple-unit);
`;

export const PlayerWatchlists = ({ slug }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { data, loading, loadMore } = usePaginatedQuery(
    PLAYER_WATCHLISTS_QUERY,
    {
      variables: {
        slug: slug || '',
      },
      connection: 'WatchlistConnection',
      skip: !slug,
    }
  );
  const { publicWatchlists, printablePlayer } = data?.anyPlayer || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        cursor: publicWatchlists?.pageInfo.endCursor,
      });
    },
    publicWatchlists?.pageInfo.hasNextPage || false,
    loading
  );

  if (!loading && !printablePlayer) {
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
          params: { slug },
        })}
      />
    );
  }

  if (!loading && !publicWatchlists?.nodes.length) {
    return (
      <StyledFullWidthContainer className="text-center">
        <HeadlineS>
          <FormattedMessage defaultMessage="No public watchlist" />
        </HeadlineS>
        <LabelL color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="This player doesn’t have any public watchlists yet" />
        </LabelL>
      </StyledFullWidthContainer>
    );
  }

  return (
    <PublicWatchlists
      publicWatchlists={publicWatchlists?.nodes || []}
      redirectTo={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
        params: { slug },
      })}
      infiniteScrollNode={<InfiniteScrollLoader />}
      loading={loading}
    />
  );
};
